import {
  DeesInputBase,
} from '@design.estate/dees-catalog';
import {
  css,
  cssManager,
  customElement,
  html,
  property,
  state,
  type TemplateResult,
} from '@design.estate/dees-element';
import type {
  IRouteChallengeConfig,
  IRoutePathPolicyBinding,
  IRouteRateLimitExceededPolicy,
  IRouteSecurity,
  IRouteSourceBinding,
} from './sz-route-card.js';

export interface ISourceProfileOption {
  id: string;
  name: string;
  description?: string;
  security?: IRouteSecurity;
  hasSourceMatches?: boolean;
  matchesAllSources?: boolean;
}

export interface IRoutePathClassOption {
  key: string;
  label: string;
  description?: string;
  defaultPatterns?: string[];
}

export interface IRouteSourcePolicyPreset {
  key: string;
  label: string;
  description?: string;
  bindings: IRouteSourceBinding[];
}

declare global {
  interface HTMLElementTagNameMap {
    'sz-input-route-source-policy': SzInputRouteSourcePolicy;
  }
}

const cloneBindings = (bindings: IRouteSourceBinding[]): IRouteSourceBinding[] => {
  return structuredClone(bindings || []);
};

const parsePositiveInteger = (value: string | number | undefined): number | undefined => {
  const parsed = typeof value === 'number' ? value : parseInt(String(value || '').trim(), 10);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : undefined;
};

const parseNonNegativeInteger = (value: string | number | undefined): number | undefined => {
  const parsed = typeof value === 'number' ? value : parseInt(String(value || '').trim(), 10);
  return Number.isInteger(parsed) && parsed >= 0 ? parsed : undefined;
};

const splitPatterns = (value: string): string[] => {
  return value
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);
};

type TProtectionControlMode = 'inherit' | 'none' | 'custom';
type TRateLimitExceededMode = '429' | 'challenge';

const protectionControlMode = (value: unknown): TProtectionControlMode => {
  if (value === null) return 'none';
  if (value === undefined) return 'inherit';
  return 'custom';
};

const defaultRateLimit = (rateLimit?: IRouteSecurity['rateLimit']): NonNullable<IRouteSecurity['rateLimit']> => {
  const onExceeded = rateLimit && rateLimit !== null ? rateLimit.onExceeded : undefined;
  return {
    enabled: true,
    maxRequests: rateLimit && 'maxRequests' in rateLimit ? rateLimit.maxRequests || 100 : 100,
    window: rateLimit && 'window' in rateLimit ? rateLimit.window || 60 : 60,
    keyBy: 'ip',
    ...(onExceeded ? { onExceeded } : {}),
  };
};

const defaultChallenge = (challenge?: IRouteSecurity['challenge']): IRouteChallengeConfig => {
  return {
    providerId: challenge && 'providerId' in challenge ? challenge.providerId || 'smartchallenge' : 'smartchallenge',
    challengeType: challenge && 'challengeType' in challenge ? challenge.challengeType || 'wait' : 'wait',
    settings: challenge && 'settings' in challenge ? challenge.settings : { waitSeconds: 0, ttlSeconds: 60 },
    clearance: challenge && 'clearance' in challenge ? challenge.clearance : { ttlSeconds: 300, bindToHost: true },
  };
};

const defaultRateLimitExceededChallenge = (
  rateLimit?: IRouteSecurity['rateLimit'],
): IRouteChallengeConfig => {
  const challenge = rateLimit && rateLimit !== null && rateLimit.onExceeded?.type === 'challenge'
    ? rateLimit.onExceeded.challenge
    : undefined;
  return defaultChallenge(challenge);
};

const normalizeRateLimitExceeded = (
  onExceeded: IRouteRateLimitExceededPolicy | undefined,
): IRouteRateLimitExceededPolicy | undefined => {
  if (!onExceeded) return undefined;
  if (onExceeded.type === 'challenge') {
    const challenge = normalizeChallenge(onExceeded.challenge);
    if (!challenge || challenge === null) return undefined;
    return {
      type: 'challenge',
      challenge,
      clearanceEffect: onExceeded.clearanceEffect === 'none' ? 'none' : 'bypass-rate-limit',
    };
  }
  return onExceeded.errorMessage?.trim()
    ? { type: '429', errorMessage: onExceeded.errorMessage.trim() }
    : undefined;
};

const normalizeRateLimit = (
  rateLimit: IRouteSecurity['rateLimit'] | undefined,
): IRouteSecurity['rateLimit'] | undefined => {
  if (rateLimit === null) {
    return null;
  }
  if (!rateLimit?.enabled) {
    return undefined;
  }
  const maxRequests = parsePositiveInteger(rateLimit.maxRequests);
  const window = parsePositiveInteger(rateLimit.window);
  if (!maxRequests || !window) {
    return undefined;
  }
  const onExceeded = normalizeRateLimitExceeded(rateLimit.onExceeded);
  const { onExceeded: _onExceeded, ...rateLimitWithoutExceeded } = rateLimit;
  return {
    ...rateLimitWithoutExceeded,
    enabled: true,
    maxRequests,
    window,
    keyBy: 'ip',
    ...(onExceeded ? { onExceeded } : {}),
  };
};

const normalizeChallenge = (
  challenge: IRouteSecurity['challenge'] | undefined,
): IRouteSecurity['challenge'] | undefined => {
  if (challenge === null) {
    return null;
  }
  if (!challenge) {
    return undefined;
  }
  const providerId = challenge.providerId?.trim();
  const challengeType = challenge.challengeType?.trim();
  if (!providerId || !challengeType) {
    return undefined;
  }
  return {
    ...challenge,
    providerId,
    challengeType,
  };
};

@customElement('sz-input-route-source-policy')
export class SzInputRouteSourcePolicy extends DeesInputBase<SzInputRouteSourcePolicy> {
  public static demo = () => html`
    <div style="padding: 24px; max-width: 980px;">
      <sz-input-route-source-policy
        key="sourcePolicy"
        label="Route Source Policy"
        .sourceProfiles=${[
          {
            id: 'trusted-profile',
            name: 'TRUSTED NETWORKS',
            description: 'Internal and operator networks',
            hasSourceMatches: true,
            matchesAllSources: false,
            security: { ipAllowList: ['10.0.0.0/8'], maxConnections: 5000 },
          },
          {
            id: 'crawler-profile',
            name: 'AI CRAWLERS',
            hasSourceMatches: true,
            matchesAllSources: false,
            security: { ipAllowList: ['203.0.113.0/24'] },
          },
          {
            id: 'public-profile',
            name: 'PUBLIC',
            hasSourceMatches: true,
            matchesAllSources: true,
            security: {
              ipAllowList: ['*'],
              rateLimit: { enabled: true, maxRequests: 120, window: 60, keyBy: 'ip' },
            },
          },
        ] satisfies ISourceProfileOption[]}
        .pathClassOptions=${[
          { key: 'git-smart-http', label: 'Git Smart HTTP' },
          { key: 'static', label: 'Static Assets' },
          { key: 'normal-html', label: 'Normal HTML' },
          { key: 'expensive-html', label: 'Expensive HTML' },
          { key: 'raw', label: 'Raw Files' },
          { key: 'archive', label: 'Archives' },
        ] satisfies IRoutePathClassOption[]}
        .value=${[
          { sourceProfileRef: 'trusted-profile', sourceProfileName: 'TRUSTED NETWORKS', onExceeded: { type: '429' as const } },
          {
            sourceProfileRef: 'public-profile',
            sourceProfileName: 'PUBLIC',
            onExceeded: { type: '429' as const },
            pathPolicies: [
              { pathClass: 'git-smart-http', rateLimit: { enabled: true, maxRequests: 1200, window: 60, keyBy: 'ip' } },
              { pathClass: 'normal-html', rateLimit: { enabled: true, maxRequests: 120, window: 60, keyBy: 'ip' } },
            ],
          },
        ] satisfies IRouteSourceBinding[]}
      ></sz-input-route-source-policy>
    </div>
  `;

  public static demoGroups = ['Routes'];

  @property({ type: Array })
  public accessor value: IRouteSourceBinding[] = [];

  @property({ type: Array })
  public accessor sourceProfiles: ISourceProfileOption[] = [];

  @property({ type: Array })
  public accessor pathClassOptions: IRoutePathClassOption[] = [];

  @property({ type: Array })
  public accessor presets: IRouteSourcePolicyPreset[] = [];

  @property({ type: Number })
  public accessor maxBindings = 16;

  @property({ type: Number })
  public accessor maxPathPoliciesPerBinding = 12;

  @property({ type: Number })
  public accessor maxPathPatternsPerPolicy = 64;

  @state()
  private accessor expandedIndex = 0;

  @state()
  private accessor notice = '';

  public static styles = [
    ...DeesInputBase.baseStyles,
    cssManager.defaultStyles,
    css`
      :host {
        display: block;
        color: ${cssManager.bdTheme('#18181b', '#f4f4f5')};
      }

      * {
        box-sizing: border-box;
      }

      .policy-shell {
        border: 1px solid ${cssManager.bdTheme('#e4e4e7', '#27272a')};
        border-radius: 10px;
        background: ${cssManager.bdTheme('#ffffff', '#09090b')};
        overflow: hidden;
      }

      .toolbar {
        display: grid;
        grid-template-columns: minmax(220px, 1fr) auto;
        gap: 10px;
        align-items: center;
        padding: 12px;
        border-bottom: 1px solid ${cssManager.bdTheme('#f4f4f5', '#18181b')};
      }

      .preset-list {
        display: flex;
        flex-wrap: wrap;
        justify-content: flex-end;
        gap: 6px;
      }

      select,
      input,
      textarea {
        width: 100%;
        min-height: 34px;
        padding: 7px 9px;
        border: 1px solid ${cssManager.bdTheme('#d4d4d8', '#3f3f46')};
        border-radius: 6px;
        background: ${cssManager.bdTheme('#ffffff', '#111113')};
        color: ${cssManager.bdTheme('#18181b', '#f4f4f5')};
        font: inherit;
        font-size: 13px;
      }

      textarea {
        min-height: 72px;
        resize: vertical;
        font-family: monospace;
      }

      input[type='checkbox'] {
        width: auto;
        min-height: 0;
      }

      button {
        border: 1px solid ${cssManager.bdTheme('#d4d4d8', '#3f3f46')};
        border-radius: 6px;
        background: ${cssManager.bdTheme('#fafafa', '#18181b')};
        color: ${cssManager.bdTheme('#27272a', '#e4e4e7')};
        min-height: 32px;
        padding: 0 10px;
        font: inherit;
        font-size: 12px;
        font-weight: 600;
        cursor: pointer;
      }

      button:hover:not(:disabled) {
        border-color: ${cssManager.bdTheme('#a1a1aa', '#71717a')};
        background: ${cssManager.bdTheme('#f4f4f5', '#27272a')};
      }

      button:disabled,
      select:disabled,
      input:disabled,
      textarea:disabled {
        opacity: 0.55;
        cursor: not-allowed;
      }

      .empty-state {
        padding: 22px;
        color: ${cssManager.bdTheme('#71717a', '#a1a1aa')};
        font-size: 13px;
        text-align: center;
      }

      .binding-row {
        border-bottom: 1px solid ${cssManager.bdTheme('#f4f4f5', '#18181b')};
      }

      .binding-row:last-child {
        border-bottom: none;
      }

      .binding-header {
        display: grid;
        grid-template-columns: 1fr auto;
        gap: 10px;
        padding: 12px;
        cursor: pointer;
        background: ${cssManager.bdTheme('#ffffff', '#09090b')};
      }

      .binding-header.expanded {
        background: ${cssManager.bdTheme('#fafafa', '#111113')};
      }

      .binding-title {
        display: flex;
        align-items: center;
        gap: 8px;
        min-width: 0;
      }

      .index-badge {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 22px;
        height: 22px;
        border-radius: 999px;
        background: ${cssManager.bdTheme('#ede9fe', 'rgba(139, 92, 246, 0.18)')};
        color: ${cssManager.bdTheme('#6d28d9', '#c4b5fd')};
        font-size: 11px;
        font-weight: 700;
        flex-shrink: 0;
      }

      .profile-name {
        font-weight: 700;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .summary {
        display: flex;
        flex-wrap: wrap;
        gap: 5px;
        margin-top: 5px;
      }

      .chip {
        display: inline-flex;
        align-items: center;
        border-radius: 999px;
        padding: 2px 7px;
        background: ${cssManager.bdTheme('#f4f4f5', 'rgba(113, 113, 122, 0.18)')};
        color: ${cssManager.bdTheme('#52525b', '#d4d4d8')};
        font-size: 11px;
        font-weight: 600;
      }

      .chip.warn {
        background: ${cssManager.bdTheme('#fef3c7', 'rgba(245, 158, 11, 0.18)')};
        color: ${cssManager.bdTheme('#92400e', '#fbbf24')};
      }

      .chip.ok {
        background: ${cssManager.bdTheme('#dcfce7', 'rgba(34, 197, 94, 0.16)')};
        color: ${cssManager.bdTheme('#166534', '#4ade80')};
      }

      .row-actions,
      .policy-actions {
        display: flex;
        align-items: center;
        gap: 6px;
      }

      .editor {
        display: flex;
        flex-direction: column;
        gap: 14px;
        padding: 14px 12px 16px;
        border-top: 1px solid ${cssManager.bdTheme('#f4f4f5', '#18181b')};
        background: ${cssManager.bdTheme('#fcfcfc', '#0f0f11')};
      }

      .section {
        display: flex;
        flex-direction: column;
        gap: 10px;
      }

      .section-title {
        font-size: 11px;
        font-weight: 700;
        letter-spacing: 0.08em;
        text-transform: uppercase;
        color: ${cssManager.bdTheme('#71717a', '#a1a1aa')};
      }

      .field-grid {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 10px;
      }

      .field {
        display: flex;
        flex-direction: column;
        gap: 5px;
      }

      .inline-field {
        display: flex;
        align-items: center;
        gap: 8px;
        font-size: 13px;
      }

      label {
        font-size: 12px;
        font-weight: 650;
        color: ${cssManager.bdTheme('#52525b', '#d4d4d8')};
      }

      .description {
        color: ${cssManager.bdTheme('#71717a', '#a1a1aa')};
        font-size: 12px;
        line-height: 1.4;
      }

      .path-policy {
        display: flex;
        flex-direction: column;
        gap: 10px;
        padding: 12px;
        border: 1px solid ${cssManager.bdTheme('#e4e4e7', '#27272a')};
        border-radius: 8px;
        background: ${cssManager.bdTheme('#ffffff', '#111113')};
      }

      .path-policy-header {
        display: grid;
        grid-template-columns: 1fr auto;
        gap: 8px;
        align-items: center;
      }

      .messages {
        display: flex;
        flex-direction: column;
        gap: 5px;
        padding: 10px 12px;
        border-top: 1px solid ${cssManager.bdTheme('#f4f4f5', '#18181b')};
        background: ${cssManager.bdTheme('#fffbeb', 'rgba(245, 158, 11, 0.08)')};
        color: ${cssManager.bdTheme('#92400e', '#fbbf24')};
        font-size: 12px;
      }

      @media (max-width: 760px) {
        .toolbar,
        .binding-header,
        .path-policy-header,
        .field-grid {
          grid-template-columns: 1fr;
        }

        .preset-list,
        .row-actions,
        .policy-actions {
          justify-content: flex-start;
          flex-wrap: wrap;
        }
      }
    `,
  ];

  public render(): TemplateResult {
    const validationMessages = this.getValidationMessages();
    return html`
      <div class="input-wrapper">
        <dees-label .label=${this.label} .infoText=${this.infoText} .required=${this.required}></dees-label>
        <div class="policy-shell">
          ${this.renderToolbar()}
          ${this.value.length === 0
            ? html`<div class="empty-state">No route-level source policy. Add source profiles in match order.</div>`
            : this.value.map((binding, index) => this.renderBinding(binding, index))}
          ${this.notice || validationMessages.length
            ? html`
                <div class="messages">
                  ${this.notice ? html`<div>${this.notice}</div>` : ''}
                  ${validationMessages.map((message) => html`<div>${message}</div>`)}
                </div>
              `
            : ''}
        </div>
        ${this.renderDescription()}
      </div>
    `;
  }

  public getValue(): IRouteSourceBinding[] {
    return this.normalizeBindings(this.value);
  }

  public setValue(value: IRouteSourceBinding[] | null | undefined): void {
    this.value = this.normalizeBindings(Array.isArray(value) ? value : []);
    this.expandedIndex = Math.min(this.expandedIndex, Math.max(0, this.value.length - 1));
    this.emitChange();
  }

  public getValidationMessages(): string[] {
    const messages: string[] = [];
    const bindings = this.value || [];
    if (bindings.length > this.maxBindings) {
      messages.push(`Source policy has ${bindings.length} bindings. Maximum is ${this.maxBindings}.`);
    }
    if (bindings.length > 0 && this.sourceProfiles.length === 0) {
      messages.push('Source policy references source profiles, but no source profiles are available.');
    }

    const seenRefs = new Set<string>();
    bindings.forEach((binding, index) => {
      const profileRef = binding.sourceProfileRef?.trim();
      if (!profileRef) {
        messages.push(`Binding ${index + 1} needs a source profile.`);
        return;
      }
      if (seenRefs.has(profileRef)) {
        messages.push(`Source profile "${this.getProfileName(profileRef)}" is selected more than once.`);
      }
      seenRefs.add(profileRef);

      const profile = this.getProfile(profileRef);
      if (this.sourceProfiles.length > 0 && !profile) {
        messages.push(`Source profile "${profileRef}" is not available.`);
      }
      if (profile?.hasSourceMatches === false) {
        messages.push(`Source profile "${profile.name}" has no source match entries.`);
      }
      if (profile?.matchesAllSources === true && index < bindings.length - 1) {
        messages.push(`Wildcard source profile "${profile.name}" must be last.`);
      }

      this.collectRateLimitMessages(binding.rateLimit, `Binding ${index + 1}`, messages);
      this.collectChallengeMessages(binding.challenge, `Binding ${index + 1}`, messages);
      if (binding.maxConnections !== undefined && parseNonNegativeInteger(binding.maxConnections) === undefined) {
        messages.push(`Binding ${index + 1} max connections must be zero or a positive integer.`);
      }

      const pathPolicies = binding.pathPolicies || [];
      if (pathPolicies.length > this.maxPathPoliciesPerBinding) {
        messages.push(`Binding ${index + 1} has more than ${this.maxPathPoliciesPerBinding} path policies.`);
      }
      pathPolicies.forEach((pathPolicy, pathIndex) => {
        const label = `Binding ${index + 1} path policy ${pathIndex + 1}`;
        if (!pathPolicy.pathClass) {
          messages.push(`${label} needs a path class.`);
        }
        if (
          this.pathClassOptions.length > 0
          && pathPolicy.pathClass
          && !this.pathClassOptions.some((option) => option.key === pathPolicy.pathClass)
        ) {
          messages.push(`${label} uses an unavailable path class "${pathPolicy.pathClass}".`);
        }
        if ((pathPolicy.pathPatterns || []).length > this.maxPathPatternsPerPolicy) {
          messages.push(`${label} has more than ${this.maxPathPatternsPerPolicy} custom patterns.`);
        }
        this.collectRateLimitMessages(pathPolicy.rateLimit, label, messages);
        this.collectChallengeMessages(pathPolicy.challenge, label, messages);
        if (pathPolicy.maxConnections !== undefined && parseNonNegativeInteger(pathPolicy.maxConnections) === undefined) {
          messages.push(`${label} max connections must be zero or a positive integer.`);
        }
      });
    });

    return messages;
  }

  public isValid(): boolean {
    return this.getValidationMessages().length === 0;
  }

  private renderToolbar(): TemplateResult {
    const availableProfiles = this.sourceProfiles.filter(
      (profile) => !this.value.some((binding) => binding.sourceProfileRef === profile.id),
    );
    return html`
      <div class="toolbar">
        <select ?disabled=${this.disabled || availableProfiles.length === 0 || this.value.length >= this.maxBindings} @change=${this.handleAddProfile}>
          <option value="">${this.value.length >= this.maxBindings ? 'Maximum bindings reached' : 'Add source profile...'}</option>
          ${availableProfiles.map((profile) => html`
            <option value=${profile.id}>${profile.name}${profile.description ? ` - ${profile.description}` : ''}</option>
          `)}
        </select>
        <div class="preset-list">
          ${this.presets.map((preset) => html`
            <button type="button" ?disabled=${this.disabled} title=${preset.description || ''} @click=${() => this.applyPreset(preset)}>
              ${preset.label}
            </button>
          `)}
        </div>
      </div>
    `;
  }

  private renderBinding(binding: IRouteSourceBinding, index: number): TemplateResult {
    const profile = this.getProfile(binding.sourceProfileRef);
    const expanded = this.expandedIndex === index;
    return html`
      <div class="binding-row">
        <div class="binding-header ${expanded ? 'expanded' : ''}" @click=${() => this.toggleExpanded(index)}>
          <div>
            <div class="binding-title">
              <span class="index-badge">${index + 1}</span>
              <span class="profile-name">${profile?.name || binding.sourceProfileName || binding.sourceProfileRef}</span>
            </div>
            <div class="summary">
              ${this.renderProfileSummary(profile)}
              ${this.renderProtectionSummaryChips(binding.rateLimit, binding.challenge)}
              ${binding.maxConnections !== undefined ? html`<span class="chip">Max conn ${binding.maxConnections}</span>` : ''}
              ${(binding.pathPolicies || []).length > 0 ? html`<span class="chip">${binding.pathPolicies!.length} path policies</span>` : ''}
            </div>
          </div>
          <div class="row-actions" @click=${(event: Event) => event.stopPropagation()}>
            <button type="button" ?disabled=${this.disabled || index === 0} @click=${() => this.moveBinding(index, -1)}>Up</button>
            <button type="button" ?disabled=${this.disabled || index === this.value.length - 1} @click=${() => this.moveBinding(index, 1)}>Down</button>
            <button type="button" ?disabled=${this.disabled} @click=${() => this.removeBinding(index)}>Remove</button>
          </div>
        </div>
        ${expanded ? this.renderBindingEditor(binding, index, profile) : ''}
      </div>
    `;
  }

  private renderBindingEditor(
    binding: IRouteSourceBinding,
    index: number,
    profile: ISourceProfileOption | undefined,
  ): TemplateResult {
    return html`
      <div class="editor">
        <div class="section">
          <div class="section-title">Inherited Source Profile</div>
          <div class="description">
            ${profile?.description || 'Route-specific policies override the selected source profile only for this route.'}
            ${profile?.security ? html`<br>${this.describeSecurity(profile.security)}` : ''}
          </div>
        </div>
        <div class="section">
          <div class="section-title">Route Policy For This Source</div>
          ${this.renderRateLimitEditor(
            binding.rateLimit,
            'Rate limit',
            (event) => this.setBindingRateLimitMode(index, (event.target as HTMLSelectElement).value as TProtectionControlMode),
            (key, event) => this.updateBindingRateLimitNumber(index, key, event),
            (event) => this.setBindingRateLimitExceededMode(index, (event.target as HTMLSelectElement).value as TRateLimitExceededMode),
            (key, event) => this.updateBindingRateLimitChallengeField(index, key, event),
          )}
          ${this.renderChallengeEditor(
            binding.challenge,
            'Browser challenge',
            (event) => this.setBindingChallengeMode(index, (event.target as HTMLSelectElement).value as TProtectionControlMode),
            (key, event) => this.updateBindingChallengeField(index, key, event),
          )}
          <div class="field-grid">
            <div class="field">
              <label>Max connections override</label>
              <input type="number" min="0" placeholder="inherit" .value=${binding.maxConnections === undefined ? '' : String(binding.maxConnections)} ?disabled=${this.disabled} @input=${(event: Event) => this.updateBindingMaxConnections(index, event)}>
            </div>
            <div class="field">
              <label>429 exceeded message</label>
              <input type="text" placeholder="default 429 response" .value=${binding.onExceeded?.errorMessage || ''} ?disabled=${this.disabled} @input=${(event: Event) => this.updateBindingExceededMessage(index, event)}>
            </div>
          </div>
        </div>
        <div class="section">
          <div class="path-policy-header">
            <div>
              <div class="section-title">Path Policies</div>
              <div class="description">Optional path-class policies override this source policy for matching paths.</div>
            </div>
            <button type="button" ?disabled=${this.disabled || (binding.pathPolicies || []).length >= this.maxPathPoliciesPerBinding} @click=${() => this.addPathPolicy(index)}>
              Add Path Policy
            </button>
          </div>
          ${(binding.pathPolicies || []).map((pathPolicy, pathIndex) => this.renderPathPolicy(pathPolicy, index, pathIndex))}
        </div>
      </div>
    `;
  }

  private renderPathPolicy(pathPolicy: IRoutePathPolicyBinding, bindingIndex: number, pathIndex: number): TemplateResult {
    return html`
      <div class="path-policy">
        <div class="path-policy-header">
          <div class="field">
            <label>Path class</label>
            <select ?disabled=${this.disabled} .value=${pathPolicy.pathClass || ''} @change=${(event: Event) => this.updatePathPolicyClass(bindingIndex, pathIndex, event)}>
              <option value="">Select path class...</option>
              ${this.pathClassOptions.map((option) => html`<option value=${option.key}>${option.label}</option>`)}
            </select>
          </div>
          <div class="policy-actions">
            <button type="button" ?disabled=${this.disabled || pathIndex === 0} @click=${() => this.movePathPolicy(bindingIndex, pathIndex, -1)}>Up</button>
            <button type="button" ?disabled=${this.disabled || pathIndex === (this.value[bindingIndex].pathPolicies || []).length - 1} @click=${() => this.movePathPolicy(bindingIndex, pathIndex, 1)}>Down</button>
            <button type="button" ?disabled=${this.disabled} @click=${() => this.removePathPolicy(bindingIndex, pathIndex)}>Remove</button>
          </div>
        </div>
        <div class="field">
          <label>Custom path patterns</label>
          <textarea placeholder="Use defaults for the selected class" ?disabled=${this.disabled} .value=${(pathPolicy.pathPatterns || []).join('\n')} @input=${(event: Event) => this.updatePathPolicyPatterns(bindingIndex, pathIndex, event)}></textarea>
        </div>
        ${this.renderRateLimitEditor(
          pathPolicy.rateLimit,
          'Rate limit',
          (event) => this.setPathRateLimitMode(bindingIndex, pathIndex, (event.target as HTMLSelectElement).value as TProtectionControlMode),
          (key, event) => this.updatePathRateLimitNumber(bindingIndex, pathIndex, key, event),
          (event) => this.setPathRateLimitExceededMode(bindingIndex, pathIndex, (event.target as HTMLSelectElement).value as TRateLimitExceededMode),
          (key, event) => this.updatePathRateLimitChallengeField(bindingIndex, pathIndex, key, event),
        )}
        ${this.renderChallengeEditor(
          pathPolicy.challenge,
          'Browser challenge',
          (event) => this.setPathChallengeMode(bindingIndex, pathIndex, (event.target as HTMLSelectElement).value as TProtectionControlMode),
          (key, event) => this.updatePathChallengeField(bindingIndex, pathIndex, key, event),
        )}
        <div class="field-grid">
          <div class="field">
            <label>Max connections override</label>
            <input type="number" min="0" placeholder="inherit" .value=${pathPolicy.maxConnections === undefined ? '' : String(pathPolicy.maxConnections)} ?disabled=${this.disabled} @input=${(event: Event) => this.updatePathMaxConnections(bindingIndex, pathIndex, event)}>
          </div>
          <div class="field">
            <label>429 exceeded message</label>
            <input type="text" placeholder="default 429 response" .value=${pathPolicy.onExceeded?.errorMessage || ''} ?disabled=${this.disabled} @input=${(event: Event) => this.updatePathExceededMessage(bindingIndex, pathIndex, event)}>
          </div>
        </div>
      </div>
    `;
  }

  private renderRateLimitEditor(
    rateLimit: IRouteSecurity['rateLimit'] | undefined,
    label: string,
    modeHandler: (event: Event) => void,
    numberHandler: (key: 'maxRequests' | 'window', event: Event) => void,
    exceededHandler: (event: Event) => void,
    challengeFieldHandler: (key: 'providerId' | 'challengeType' | 'ttlSeconds', event: Event) => void,
  ): TemplateResult {
    const mode = protectionControlMode(rateLimit);
    const customRateLimit = rateLimit && rateLimit !== null ? rateLimit : undefined;
    const exceededMode: TRateLimitExceededMode = customRateLimit?.onExceeded?.type === 'challenge' ? 'challenge' : '429';
    const exceededChallenge = defaultRateLimitExceededChallenge(customRateLimit);
    return html`
      <div class="field">
        <label>${label}</label>
        <select ?disabled=${this.disabled} .value=${mode} @change=${modeHandler}>
          <option value="inherit">Inherit effective default</option>
          <option value="none">No rate limit</option>
          <option value="custom">Custom rate limit</option>
        </select>
        <div class="description">
          ${mode === 'inherit' ? 'Use the source profile or parent policy default.' : ''}
          ${mode === 'none' ? 'Explicitly clear inherited rate limiting for this scope.' : ''}
          ${mode === 'custom' ? 'Apply this limit before forwarding matching requests.' : ''}
        </div>
      </div>
      ${mode === 'custom'
        ? html`
            <div class="field-grid">
              <div class="field">
                <label>Max requests</label>
                <input type="number" min="1" .value=${String(customRateLimit?.maxRequests || '')} ?disabled=${this.disabled} @input=${(event: Event) => numberHandler('maxRequests', event)}>
              </div>
              <div class="field">
                <label>Window seconds</label>
                <input type="number" min="1" .value=${String(customRateLimit?.window || '')} ?disabled=${this.disabled} @input=${(event: Event) => numberHandler('window', event)}>
              </div>
              <div class="field">
                <label>When exceeded</label>
                <select ?disabled=${this.disabled} .value=${exceededMode} @change=${exceededHandler}>
                  <option value="429">Return 429</option>
                  <option value="challenge">Browser challenge</option>
                </select>
              </div>
            </div>
            ${exceededMode === 'challenge'
              ? html`
                  <div class="field-grid">
                    <div class="field">
                      <label>Challenge provider ID</label>
                      <input type="text" placeholder="smartchallenge" .value=${exceededChallenge.providerId || ''} ?disabled=${this.disabled} @input=${(event: Event) => challengeFieldHandler('providerId', event)}>
                    </div>
                    <div class="field">
                      <label>Challenge type</label>
                      <input type="text" placeholder="wait" .value=${exceededChallenge.challengeType || ''} ?disabled=${this.disabled} @input=${(event: Event) => challengeFieldHandler('challengeType', event)}>
                    </div>
                    <div class="field">
                      <label>Clearance TTL seconds</label>
                      <input type="number" min="1" placeholder="300" .value=${String(exceededChallenge.clearance?.ttlSeconds || '')} ?disabled=${this.disabled} @input=${(event: Event) => challengeFieldHandler('ttlSeconds', event)}>
                    </div>
                  </div>
                `
              : ''}
          `
        : ''}
    `;
  }

  private renderChallengeEditor(
    challenge: IRouteSecurity['challenge'] | undefined,
    label: string,
    modeHandler: (event: Event) => void,
    fieldHandler: (key: 'providerId' | 'challengeType' | 'ttlSeconds', event: Event) => void,
  ): TemplateResult {
    const mode = protectionControlMode(challenge);
    const customChallenge = challenge && challenge !== null ? challenge : undefined;
    return html`
      <div class="field">
        <label>${label}</label>
        <select ?disabled=${this.disabled} .value=${mode} @change=${modeHandler}>
          <option value="inherit">Inherit effective default</option>
          <option value="none">No browser challenge</option>
          <option value="custom">Custom browser challenge</option>
        </select>
        <div class="description">
          ${mode === 'inherit' ? 'Use the source profile or parent policy challenge default.' : ''}
          ${mode === 'none' ? 'Explicitly clear inherited browser challenge for this scope.' : ''}
          ${mode === 'custom' ? 'Challenge matching browser-visible HTTP requests.' : ''}
        </div>
      </div>
      ${mode === 'custom'
        ? html`
            <div class="field-grid">
              <div class="field">
                <label>Provider ID</label>
                <input type="text" placeholder="smartchallenge" .value=${customChallenge?.providerId || ''} ?disabled=${this.disabled} @input=${(event: Event) => fieldHandler('providerId', event)}>
              </div>
              <div class="field">
                <label>Challenge type</label>
                <input type="text" placeholder="wait" .value=${customChallenge?.challengeType || ''} ?disabled=${this.disabled} @input=${(event: Event) => fieldHandler('challengeType', event)}>
              </div>
              <div class="field">
                <label>Clearance TTL seconds</label>
                <input type="number" min="1" placeholder="300" .value=${String(customChallenge?.clearance?.ttlSeconds || '')} ?disabled=${this.disabled} @input=${(event: Event) => fieldHandler('ttlSeconds', event)}>
              </div>
            </div>
          `
        : ''}
    `;
  }

  private renderProfileSummary(profile: ISourceProfileOption | undefined): TemplateResult {
    if (!profile) {
      return html`<span class="chip warn">Profile unavailable</span>`;
    }
    if (profile.hasSourceMatches === false) {
      return html`<span class="chip warn">No source matches</span>`;
    }
    if (profile.matchesAllSources === true) {
      return html`<span class="chip ok">Wildcard fallback</span>`;
    }
    return html`<span class="chip ok">Source matched</span>`;
  }

  private renderProtectionSummaryChips(
    rateLimit: IRouteSecurity['rateLimit'] | undefined,
    challenge: IRouteSecurity['challenge'] | undefined,
  ): TemplateResult {
    return html`
      ${rateLimit === null ? html`<span class="chip warn">No rate limit</span>` : ''}
      ${rateLimit && rateLimit !== null && rateLimit.enabled ? html`<span class="chip">Rate ${this.describeRateLimit(rateLimit)}</span>` : ''}
      ${challenge === null ? html`<span class="chip warn">No challenge</span>` : ''}
      ${challenge && challenge !== null ? html`<span class="chip">Challenge ${challenge.providerId}/${challenge.challengeType}</span>` : ''}
    `;
  }

  private handleAddProfile = (event: Event) => {
    const select = event.target as HTMLSelectElement;
    const profileRef = select.value;
    select.value = '';
    if (!profileRef || this.disabled) return;
    if (this.value.length >= this.maxBindings) {
      this.notice = `Source policy can contain at most ${this.maxBindings} bindings.`;
      return;
    }
    if (this.value.some((binding) => binding.sourceProfileRef === profileRef)) {
      this.notice = `Source profile "${this.getProfileName(profileRef)}" is already selected.`;
      return;
    }
    const profile = this.getProfile(profileRef);
    const next = cloneBindings(this.value);
    next.push({
      sourceProfileRef: profileRef,
      ...(profile?.name ? { sourceProfileName: profile.name } : {}),
      onExceeded: { type: '429' as const },
    });
    this.expandedIndex = next.length - 1;
    this.setWorkingValue(next);
  };

  private applyPreset(preset: IRouteSourcePolicyPreset): void {
    if (this.disabled) return;
    this.expandedIndex = 0;
    this.setWorkingValue(cloneBindings(preset.bindings));
  }

  private toggleExpanded(index: number): void {
    this.expandedIndex = this.expandedIndex === index ? -1 : index;
  }

  private moveBinding(index: number, direction: -1 | 1): void {
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= this.value.length) return;
    const next = cloneBindings(this.value);
    const [binding] = next.splice(index, 1);
    next.splice(targetIndex, 0, binding);
    this.expandedIndex = targetIndex;
    this.setWorkingValue(next);
  }

  private removeBinding(index: number): void {
    const next = cloneBindings(this.value);
    next.splice(index, 1);
    this.expandedIndex = Math.min(index, Math.max(0, next.length - 1));
    this.setWorkingValue(next);
  }

  private setBindingRateLimitMode(index: number, mode: TProtectionControlMode): void {
    this.updateBinding(index, (binding) => {
      if (mode === 'inherit') {
        delete binding.rateLimit;
        return;
      }
      if (mode === 'none') {
        binding.rateLimit = null;
        return;
      }
      binding.rateLimit = defaultRateLimit(binding.rateLimit);
    });
  }

  private updateBindingRateLimitNumber(index: number, key: 'maxRequests' | 'window', event: Event): void {
    const value = parseInt((event.target as HTMLInputElement).value, 10);
    this.updateBinding(index, (binding) => {
      const current = defaultRateLimit(binding.rateLimit);
      binding.rateLimit = {
        ...current,
        [key]: Number.isInteger(value) ? value : 0,
      };
    });
  }

  private setBindingRateLimitExceededMode(index: number, mode: TRateLimitExceededMode): void {
    this.updateBinding(index, (binding) => {
      const rateLimit = defaultRateLimit(binding.rateLimit);
      if (mode === 'challenge') {
        rateLimit.onExceeded = {
          type: 'challenge',
          challenge: defaultRateLimitExceededChallenge(binding.rateLimit),
          clearanceEffect: 'bypass-rate-limit',
        };
      } else {
        delete rateLimit.onExceeded;
      }
      binding.rateLimit = rateLimit;
    });
  }

  private updateBindingRateLimitChallengeField(
    index: number,
    key: 'providerId' | 'challengeType' | 'ttlSeconds',
    event: Event,
  ): void {
    const rawValue = (event.target as HTMLInputElement).value.trim();
    this.updateBinding(index, (binding) => {
      const rateLimit = defaultRateLimit(binding.rateLimit);
      const challenge = defaultRateLimitExceededChallenge(rateLimit);
      if (key === 'ttlSeconds') {
        const ttlSeconds = parsePositiveInteger(rawValue);
        challenge.clearance = {
          ...(challenge.clearance || {}),
          ...(ttlSeconds ? { ttlSeconds } : {}),
        };
      } else {
        challenge[key] = rawValue;
      }
      rateLimit.onExceeded = {
        type: 'challenge',
        challenge,
        clearanceEffect: rateLimit.onExceeded?.clearanceEffect === 'none' ? 'none' : 'bypass-rate-limit',
      };
      binding.rateLimit = rateLimit;
    });
  }

  private setBindingChallengeMode(index: number, mode: TProtectionControlMode): void {
    this.updateBinding(index, (binding) => {
      if (mode === 'inherit') {
        delete binding.challenge;
        return;
      }
      if (mode === 'none') {
        binding.challenge = null;
        return;
      }
      binding.challenge = defaultChallenge(binding.challenge);
    });
  }

  private updateBindingChallengeField(
    index: number,
    key: 'providerId' | 'challengeType' | 'ttlSeconds',
    event: Event,
  ): void {
    const rawValue = (event.target as HTMLInputElement).value.trim();
    this.updateBinding(index, (binding) => {
      const challenge = defaultChallenge(binding.challenge);
      if (key === 'ttlSeconds') {
        const ttlSeconds = parsePositiveInteger(rawValue);
        challenge.clearance = {
          ...(challenge.clearance || {}),
          ...(ttlSeconds ? { ttlSeconds } : {}),
        };
      } else {
        challenge[key] = rawValue;
      }
      binding.challenge = challenge;
    });
  }

  private updateBindingMaxConnections(index: number, event: Event): void {
    const rawValue = (event.target as HTMLInputElement).value.trim();
    this.updateBinding(index, (binding) => {
      if (!rawValue) {
        delete binding.maxConnections;
        return;
      }
      binding.maxConnections = parseInt(rawValue, 10);
    });
  }

  private updateBindingExceededMessage(index: number, event: Event): void {
    const message = (event.target as HTMLInputElement).value.trim();
    this.updateBinding(index, (binding) => {
      binding.onExceeded = {
        type: '429' as const,
        ...(message ? { errorMessage: message } : {}),
      };
    });
  }

  private addPathPolicy(bindingIndex: number): void {
    this.updateBinding(bindingIndex, (binding) => {
      const pathPolicies = [...(binding.pathPolicies || [])];
      if (pathPolicies.length >= this.maxPathPoliciesPerBinding) return;
      pathPolicies.push({
        pathClass: this.pathClassOptions[0]?.key || '',
      });
      binding.pathPolicies = pathPolicies;
    });
  }

  private movePathPolicy(bindingIndex: number, pathIndex: number, direction: -1 | 1): void {
    this.updateBinding(bindingIndex, (binding) => {
      const pathPolicies = [...(binding.pathPolicies || [])];
      const targetIndex = pathIndex + direction;
      if (targetIndex < 0 || targetIndex >= pathPolicies.length) return;
      const [pathPolicy] = pathPolicies.splice(pathIndex, 1);
      pathPolicies.splice(targetIndex, 0, pathPolicy);
      binding.pathPolicies = pathPolicies;
    });
  }

  private removePathPolicy(bindingIndex: number, pathIndex: number): void {
    this.updateBinding(bindingIndex, (binding) => {
      const pathPolicies = [...(binding.pathPolicies || [])];
      pathPolicies.splice(pathIndex, 1);
      if (pathPolicies.length) {
        binding.pathPolicies = pathPolicies;
      } else {
        delete binding.pathPolicies;
      }
    });
  }

  private updatePathPolicyClass(bindingIndex: number, pathIndex: number, event: Event): void {
    this.updatePathPolicy(bindingIndex, pathIndex, (pathPolicy) => {
      pathPolicy.pathClass = (event.target as HTMLSelectElement).value;
    });
  }

  private updatePathPolicyPatterns(bindingIndex: number, pathIndex: number, event: Event): void {
    const patterns = splitPatterns((event.target as HTMLTextAreaElement).value);
    this.updatePathPolicy(bindingIndex, pathIndex, (pathPolicy) => {
      if (patterns.length) {
        pathPolicy.pathPatterns = patterns;
      } else {
        delete pathPolicy.pathPatterns;
      }
    });
  }

  private setPathRateLimitMode(bindingIndex: number, pathIndex: number, mode: TProtectionControlMode): void {
    this.updatePathPolicy(bindingIndex, pathIndex, (pathPolicy) => {
      if (mode === 'inherit') {
        delete pathPolicy.rateLimit;
        return;
      }
      if (mode === 'none') {
        pathPolicy.rateLimit = null;
        return;
      }
      pathPolicy.rateLimit = defaultRateLimit(pathPolicy.rateLimit);
    });
  }

  private updatePathRateLimitNumber(
    bindingIndex: number,
    pathIndex: number,
    key: 'maxRequests' | 'window',
    event: Event,
  ): void {
    const value = parseInt((event.target as HTMLInputElement).value, 10);
    this.updatePathPolicy(bindingIndex, pathIndex, (pathPolicy) => {
      const current = defaultRateLimit(pathPolicy.rateLimit);
      pathPolicy.rateLimit = {
        ...current,
        [key]: Number.isInteger(value) ? value : 0,
      };
    });
  }

  private setPathRateLimitExceededMode(
    bindingIndex: number,
    pathIndex: number,
    mode: TRateLimitExceededMode,
  ): void {
    this.updatePathPolicy(bindingIndex, pathIndex, (pathPolicy) => {
      const rateLimit = defaultRateLimit(pathPolicy.rateLimit);
      if (mode === 'challenge') {
        rateLimit.onExceeded = {
          type: 'challenge',
          challenge: defaultRateLimitExceededChallenge(pathPolicy.rateLimit),
          clearanceEffect: 'bypass-rate-limit',
        };
      } else {
        delete rateLimit.onExceeded;
      }
      pathPolicy.rateLimit = rateLimit;
    });
  }

  private updatePathRateLimitChallengeField(
    bindingIndex: number,
    pathIndex: number,
    key: 'providerId' | 'challengeType' | 'ttlSeconds',
    event: Event,
  ): void {
    const rawValue = (event.target as HTMLInputElement).value.trim();
    this.updatePathPolicy(bindingIndex, pathIndex, (pathPolicy) => {
      const rateLimit = defaultRateLimit(pathPolicy.rateLimit);
      const challenge = defaultRateLimitExceededChallenge(rateLimit);
      if (key === 'ttlSeconds') {
        const ttlSeconds = parsePositiveInteger(rawValue);
        challenge.clearance = {
          ...(challenge.clearance || {}),
          ...(ttlSeconds ? { ttlSeconds } : {}),
        };
      } else {
        challenge[key] = rawValue;
      }
      rateLimit.onExceeded = {
        type: 'challenge',
        challenge,
        clearanceEffect: rateLimit.onExceeded?.clearanceEffect === 'none' ? 'none' : 'bypass-rate-limit',
      };
      pathPolicy.rateLimit = rateLimit;
    });
  }

  private setPathChallengeMode(
    bindingIndex: number,
    pathIndex: number,
    mode: TProtectionControlMode,
  ): void {
    this.updatePathPolicy(bindingIndex, pathIndex, (pathPolicy) => {
      if (mode === 'inherit') {
        delete pathPolicy.challenge;
        return;
      }
      if (mode === 'none') {
        pathPolicy.challenge = null;
        return;
      }
      pathPolicy.challenge = defaultChallenge(pathPolicy.challenge);
    });
  }

  private updatePathChallengeField(
    bindingIndex: number,
    pathIndex: number,
    key: 'providerId' | 'challengeType' | 'ttlSeconds',
    event: Event,
  ): void {
    const rawValue = (event.target as HTMLInputElement).value.trim();
    this.updatePathPolicy(bindingIndex, pathIndex, (pathPolicy) => {
      const challenge = defaultChallenge(pathPolicy.challenge);
      if (key === 'ttlSeconds') {
        const ttlSeconds = parsePositiveInteger(rawValue);
        challenge.clearance = {
          ...(challenge.clearance || {}),
          ...(ttlSeconds ? { ttlSeconds } : {}),
        };
      } else {
        challenge[key] = rawValue;
      }
      pathPolicy.challenge = challenge;
    });
  }

  private updatePathMaxConnections(bindingIndex: number, pathIndex: number, event: Event): void {
    const rawValue = (event.target as HTMLInputElement).value.trim();
    this.updatePathPolicy(bindingIndex, pathIndex, (pathPolicy) => {
      if (!rawValue) {
        delete pathPolicy.maxConnections;
        return;
      }
      pathPolicy.maxConnections = parseInt(rawValue, 10);
    });
  }

  private updatePathExceededMessage(bindingIndex: number, pathIndex: number, event: Event): void {
    const message = (event.target as HTMLInputElement).value.trim();
    this.updatePathPolicy(bindingIndex, pathIndex, (pathPolicy) => {
      if (message) {
        pathPolicy.onExceeded = { type: '429' as const, errorMessage: message };
      } else {
        delete pathPolicy.onExceeded;
      }
    });
  }

  private updateBinding(index: number, updater: (binding: IRouteSourceBinding) => void): void {
    const next = cloneBindings(this.value);
    if (!next[index]) return;
    updater(next[index]);
    this.setWorkingValue(next);
  }

  private updatePathPolicy(
    bindingIndex: number,
    pathIndex: number,
    updater: (pathPolicy: IRoutePathPolicyBinding) => void,
  ): void {
    this.updateBinding(bindingIndex, (binding) => {
      const pathPolicies = [...(binding.pathPolicies || [])];
      if (!pathPolicies[pathIndex]) return;
      updater(pathPolicies[pathIndex]);
      binding.pathPolicies = pathPolicies;
    });
  }

  private setWorkingValue(bindings: IRouteSourceBinding[]): void {
    this.value = cloneBindings(bindings);
    this.notice = '';
    this.emitChange();
  }

  private emitChange(): void {
    const value = this.getValue();
    const validationMessages = this.getValidationMessages();
    this.changeSubject.next(this);
    this.dispatchEvent(new CustomEvent('change', {
      detail: { value, validationMessages },
      bubbles: true,
      composed: true,
    }));
  }

  private normalizeBindings(bindings: IRouteSourceBinding[]): IRouteSourceBinding[] {
    return cloneBindings(bindings)
      .map((binding) => this.normalizeBinding(binding))
      .filter((binding): binding is IRouteSourceBinding => Boolean(binding));
  }

  private normalizeBinding(binding: IRouteSourceBinding): IRouteSourceBinding | undefined {
    const sourceProfileRef = binding.sourceProfileRef?.trim();
    if (!sourceProfileRef) return undefined;
    const profile = this.getProfile(sourceProfileRef);
    const rateLimit = normalizeRateLimit(binding.rateLimit);
    const challenge = normalizeChallenge(binding.challenge);
    const maxConnections = parseNonNegativeInteger(binding.maxConnections);
    const pathPolicies = this.normalizePathPolicies(binding.pathPolicies || []);
    const exceededMessage = binding.onExceeded?.errorMessage?.trim();
    return {
      ...(binding.id?.trim() ? { id: binding.id.trim() } : {}),
      sourceProfileRef,
      ...(profile?.name ? { sourceProfileName: profile.name } : binding.sourceProfileName ? { sourceProfileName: binding.sourceProfileName } : {}),
      ...(rateLimit !== undefined ? { rateLimit } : {}),
      ...(challenge !== undefined ? { challenge } : {}),
      ...(maxConnections !== undefined ? { maxConnections } : {}),
      onExceeded: {
        type: '429' as const,
        ...(exceededMessage ? { errorMessage: exceededMessage } : {}),
      },
      ...(pathPolicies.length ? { pathPolicies } : {}),
    };
  }

  private normalizePathPolicies(pathPolicies: IRoutePathPolicyBinding[]): IRoutePathPolicyBinding[] {
    return cloneBindings([{ sourceProfileRef: 'path-policy-wrapper', pathPolicies }])[0].pathPolicies!
      .map((pathPolicy) => {
        const pathClass = pathPolicy.pathClass?.trim();
        if (!pathClass) return undefined;
        const rateLimit = normalizeRateLimit(pathPolicy.rateLimit);
        const challenge = normalizeChallenge(pathPolicy.challenge);
        const maxConnections = parseNonNegativeInteger(pathPolicy.maxConnections);
        const exceededMessage = pathPolicy.onExceeded?.errorMessage?.trim();
        const normalizedPolicy: IRoutePathPolicyBinding = {
          ...(pathPolicy.id?.trim() ? { id: pathPolicy.id.trim() } : {}),
          pathClass,
          ...(pathPolicy.pathPatterns?.length ? { pathPatterns: pathPolicy.pathPatterns.map((pattern) => pattern.trim()).filter(Boolean) } : {}),
          ...(rateLimit !== undefined ? { rateLimit } : {}),
          ...(challenge !== undefined ? { challenge } : {}),
          ...(maxConnections !== undefined ? { maxConnections } : {}),
          ...(pathPolicy.onExceeded || exceededMessage ? {
            onExceeded: {
              type: '429' as const,
              ...(exceededMessage ? { errorMessage: exceededMessage } : {}),
            },
          } : {}),
        };
        return normalizedPolicy;
      })
      .filter((pathPolicy): pathPolicy is IRoutePathPolicyBinding => Boolean(pathPolicy));
  }

  private collectRateLimitMessages(
    rateLimit: IRouteSecurity['rateLimit'] | undefined,
    label: string,
    messages: string[],
  ): void {
    if (rateLimit === null) return;
    if (!rateLimit?.enabled) return;
    if (!parsePositiveInteger(rateLimit.maxRequests)) {
      messages.push(`${label} rate limit max requests must be a positive integer.`);
    }
    if (!parsePositiveInteger(rateLimit.window)) {
      messages.push(`${label} rate limit window must be a positive integer.`);
    }
    if (rateLimit.onExceeded?.type === 'challenge') {
      this.collectChallengeMessages(
        rateLimit.onExceeded.challenge,
        `${label} rate-limit challenge`,
        messages,
      );
    }
  }

  private collectChallengeMessages(
    challenge: IRouteSecurity['challenge'] | undefined,
    label: string,
    messages: string[],
  ): void {
    if (challenge === null || challenge === undefined) return;
    if (!challenge.providerId?.trim()) {
      messages.push(`${label} challenge provider ID is required.`);
    }
    if (!challenge.challengeType?.trim()) {
      messages.push(`${label} challenge type is required.`);
    }
    if (
      challenge.clearance?.ttlSeconds !== undefined
      && !parsePositiveInteger(challenge.clearance.ttlSeconds)
    ) {
      messages.push(`${label} challenge clearance TTL must be a positive integer.`);
    }
  }

  private getProfile(profileRef: string | undefined): ISourceProfileOption | undefined {
    return this.sourceProfiles.find((profile) => profile.id === profileRef);
  }

  private getProfileName(profileRef: string): string {
    const profile = this.getProfile(profileRef);
    return profile?.name || profileRef;
  }

  private describeSecurity(security: IRouteSecurity): string {
    const parts: string[] = [];
    if (security.ipAllowList?.length) parts.push(`IP allow: ${security.ipAllowList.join(', ')}`);
    if (security.maxConnections !== undefined) parts.push(`max connections: ${security.maxConnections}`);
    if (security.rateLimit === null) parts.push('rate: none');
    if (security.rateLimit && security.rateLimit.enabled) parts.push(`rate: ${this.describeRateLimit(security.rateLimit)}`);
    if (security.challenge === null) parts.push('challenge: none');
    if (security.challenge) parts.push(`challenge: ${security.challenge.providerId}/${security.challenge.challengeType}`);
    return parts.join(' | ') || 'No inherited controls shown.';
  }

  private describeRateLimit(rateLimit: IRouteSecurity['rateLimit']): string {
    if (rateLimit === null) return 'none';
    if (!rateLimit?.enabled) return 'inherit';
    const exceeded = rateLimit.onExceeded?.type === 'challenge' ? ' then challenge' : '';
    return `${rateLimit.maxRequests}/${rateLimit.window}s per IP${exceeded}`;
  }
}
